import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FeatureFlags } from '@feature-flags/feature-flags.const';
import { injectQuery, injectQueryClient, QueryObserverResult } from '@ngneat/query';
import { ContextService } from 'app/core/services/context.service';
import { ApiResponse, Client, Site, SiteScacCodes } from 'app/shared/types';
import { minutesToMilliseconds } from 'date-fns';
import { environment } from 'environment/environment';
import { map, Observable, tap } from 'rxjs';
import { CACHE_KEYS } from '../enums/cache-keys';
import { Result } from '../types/cache';
import { BaseApiService } from './base-api.service';
import { CONTROLLERS } from './controllers';

@Injectable({
  providedIn: 'root',
})
export class ClientsService extends BaseApiService<
  ApiResponse<{ clients: Client[] }>,
  ApiResponse<{ client: Client }>,
  ApiResponse<{ id: string }>
> {
  private contextService = inject(ContextService);
  private queryClient = injectQueryClient();
  private query = injectQuery();

  protected readonly carrierManagementImprovement = this.contextService.isFeatureFlagEnabled(
    FeatureFlags.CarrierManagementImprovement,
  );

  constructor(public override http: HttpClient) {
    super(http, environment.siteManagementApiBaseUrl);
  }

  public override controllerName(): string {
    return CONTROLLERS.clients;
  }

  public override getById(id: string): Observable<ApiResponse<{ client: Client }>> {
    if (!this.carrierManagementImprovement) {
      return super.getById(id);
    }
    const params = new HttpParams().set('api-version', '2.0');
    return this.http
      .get<ApiResponse<{ client: Client }>>(`${this.api}/${this.controllerName()}/${id}`, { params })
      .pipe(
        tap(() => {
          this.queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.CLIENTS] as const });
        }),
      );
  }

  public override insert(client: Client): Observable<ApiResponse<{ id: string }>> {
    if (!this.carrierManagementImprovement) {
      return super.insert(client).pipe(
        tap(() => {
          this.queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.CLIENTS] as const });
        }),
      );
    }

    const params = new HttpParams().set('api-version', '2.0');
    return this.http.post<ApiResponse<{ id: string }>>(`${this.api}/${this.controllerName()}`, client, { params }).pipe(
      tap(() => {
        this.queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.CLIENTS] as const });
      }),
    );
  }

  public override update(id: string, client: Client): Observable<ApiResponse<{ id: string }>> {
    if (!this.carrierManagementImprovement) {
      return super.update(id, client).pipe(
        tap(() => {
          this.queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.CLIENTS] as const });
        }),
      );
    }
    const params = new HttpParams().set('api-version', '2.0');
    return this.http
      .put<ApiResponse<{ id: string }>>(`${this.api}/${this.controllerName()}/${id}`, client, {
        params,
      })
      .pipe(
        tap(() => {
          this.queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.CLIENTS] as const });
        }),
      );
  }

  public getSitesByClient(clientId: string | null): Observable<ApiResponse<{ sites: Site[] }>> {
    return this.http.get<ApiResponse<{ sites: Site[] }>>(`${this.api}/${this.controllerName()}/${clientId}/sites`);
  }

  public getScacCodes(id: string): Observable<ApiResponse<{ scacs: SiteScacCodes[] }>> {
    return this.http.get<ApiResponse<{ scacs: SiteScacCodes[] }>>(`${this.api}/${this.controllerName()}/${id}/scacs`);
  }

  public getClients(): Result<QueryObserverResult<Client[], Error>> {
    return this.query({
      queryKey: [CACHE_KEYS.CLIENTS] as const,
      queryFn: () =>
        this.http
          .get<ApiResponse<{ clients: Client[] }>>(`${this.api}/${this.controllerName()}`)
          .pipe(map((x) => x.value.clients)),
      staleTime: minutesToMilliseconds(10),
    });
  }
}
